import {useEffect, useMemo, useState} from 'react'
import {BehaviorSubject, of, timer} from 'rxjs'
import {CFModel, CFValue} from 'module/CustomField/type/CFType'
import {filter, map, mergeMap, switchMap, tap} from 'rxjs/operators'
import {CFComponentProps} from 'module/CustomField/type/CFComponentType'
import {CustomFieldTO} from 'generated/JavaTO'
import {CF} from 'module/CustomField/util/CustomField'
import {DqElement, searchDqWithCFDef} from 'module/Dq/searchDq'
import {ByValueCache} from 'module/Slct/type/ByValueCache'
import {isDqSearchable} from 'module/Dq/util/DqParser'


function mapResult<ValueType extends string | number>(x: DqElement<ValueType>, cfDef: CustomFieldTO) {
	let value = CF.transformForView(cfDef, x.value) as ValueType;
	return {
		...x,
		value,
	} as DqElement<ValueType>
}

interface DqSearchRequest {
	searchTerm: CFValue
	model: CFModel
	onlyByValueCache?: boolean // use for non-view-initiated searches
}

interface DqSearchResponse<ValueType extends string | number> {
	request: DqSearchRequest
	result: DqElement[]
	byValueCache: ByValueCache<ValueType>
	searchable: boolean
}

export function useDqSearchRx<ValueType extends string | number>({cfDef, model, pos, isAze}: Pick<CFComponentProps<CFValue>, 'cfDef' | 'model' | 'pos' | 'isAze'>) {
	const [byValueCache, setByValueCache] = useState<ByValueCache<ValueType>>({})
	const [suggestions, setSuggestions] = useState<DqElement[]>([])
	const [loading, setLoading] = useState(false)
	const {searchSubject, responseSubject} = useMemo(() => ({
		searchSubject: new BehaviorSubject<DqSearchRequest>({searchTerm: '', model}),
		responseSubject: new BehaviorSubject<null | DqSearchResponse<ValueType>>(null),
	}), []);
	useEffect(() => {
		const subscription = searchSubject.pipe(
			filter(() => Boolean(cfDef.dqKey)),
			switchMap((req) => {
				if (req.onlyByValueCache) return of(req)
				else return timer(100).pipe(map(() => req))
			}),
			tap(() => setLoading(true)),
			mergeMap((request) => {
				const {searchTerm, model} = request
				return searchDqWithCFDef(cfDef, model, pos, searchTerm as string, {isAze})
					.then((result) => ({result, request}))
			}),
			tap((ret) => {
				const {model} = ret.request
				setLoading(false)
				const dqResult = ret.result.map(m => mapResult(m, cfDef))
				setByValueCache(byValue => {
					byValue = dqResult.reduce((prev, curr: DqElement<ValueType>) => {
						if (curr && (curr.value || curr.value === 0)) prev[curr.value] = curr
						return prev
					}, {...byValue} as ByValueCache<ValueType>)
					responseSubject.next({
						...ret,
						byValueCache: byValue,
						searchable: isDqSearchable(cfDef.dqKey),
					})
					const rawValue = CF.getValueFrom(cfDef, model, {pos, forView: false}) as CFValue
					if ((rawValue || rawValue === 0) && !byValue?.[rawValue] && ret.request.searchTerm !== rawValue) {
						//response did not find the current value
						searchSubject.next({searchTerm: rawValue, model, onlyByValueCache: true})
					}
					return byValue
				})
				//add raw when not present
				if (!ret.request.onlyByValueCache) setSuggestions(dqResult)
			}),
		).subscribe();
		return () => subscription.unsubscribe()
	}, [cfDef, searchSubject, responseSubject])
	return {
		searchSubject,
		suggestions,
		byValueCache,
		loading,
		responseSubject,
	}
}